// ─────────────────────────────────────────────────────────────
// ACCURATE JSON-TO-HTML PAGINATION ENGINE
// ─────────────────────────────────────────────────────────────
// Extracted from TipTap.tsx into its own dependency-free module (no
// React/@tiptap imports) so it can be unit tested in isolation and reused
// without pulling in the whole editor component tree.

import { findTocBlockRanges, isForcedPageStartText } from "./headingUtils";

export const renderSingleElementToHtml = (element: any): string => {
  switch (element.type) {
    case "paragraph": {
      const alignStyle = element.alignment
        ? `text-align: ${element.alignment}; `
        : "";
      const finalStyles = `${alignStyle}`.trim();
      const styleAttr = finalStyles ? ` style="${finalStyles}"` : "";

      if (element.data && Array.isArray(element.data)) {
        const dataHtml = element.data
          .map((item: any) => {
            let text = item.label || "";
            if (item.value) text += ` ${item.value}`;
            return text
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;");
          })
          .join("<br />");

        return `<p${styleAttr}>${dataHtml || "&nbsp;"}</p>`;
      }

      const textRuns = (element.runs || [])
        .map((run: any) => {
          let text = run.text;
          if (text === undefined || text === null) return "";
          if (text === "\t") return "&nbsp;&nbsp;&nbsp;&nbsp;";

          text = text
            .replace(/\n/g, "<br />")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

          if (run.bold) text = `<strong>${text}</strong>`;
          if (run.italic) text = `<em>${text}</em>`;
          if (run.underline) text = `<u>${text}</u>`;
          if (run.strike) text = `<s>${text}</s>`;

          const spanStyles: string[] = [];
          if (run.color) {
            const validColor =
              run.color.startsWith("#") || run.color.startsWith("rgb")
                ? run.color
                : `#${run.color}`;
            spanStyles.push(`color: ${validColor}`);
          }
          if (run.font_size) spanStyles.push(`font-size: ${run.font_size}pt`);
          if (run.font_family)
            spanStyles.push(`font-family: ${run.font_family}`);

          if (run.highlight) {
            const validHighlight =
              run.highlight.startsWith("#") || run.highlight.startsWith("rgb")
                ? run.highlight
                : `#${run.highlight}`;
            spanStyles.push(`background-color: ${validHighlight}`);
          }

          if (spanStyles.length > 0) {
            text = `<span style="${spanStyles.join("; ")}">${text}</span>`;
          }
          if (run.link) {
            // Re-inject the data attributes so they persist on save/reload
            const actionAttr = run.link["data-action"]
              ? ` data-action="${run.link["data-action"]}"`
              : "";

            // CRITICAL FIX: Escape single quotes in the JSON string so it doesn't break the HTML attribute
            const escapedImages = run.link["data-images"]
              ? run.link["data-images"].replace(/'/g, "&apos;")
              : "";
            const imagesAttr = escapedImages
              ? ` data-images='${escapedImages}'`
              : "";

            text = `<a href="${run.link.href || "#"}"${actionAttr}${imagesAttr}>${text}</a>`;
          }

          return text;
        })
        .join("");

      return `<p${styleAttr}>${textRuns || "&nbsp;"}</p>`;
    }

    case "table":
    case "table-borderless": {
      if (!element.rows || !Array.isArray(element.rows)) return "";
      // A row can either be a plain array of cells or an object of shape
      // { cells: [...] }. Normalize so column/header detection works either way.
      const getRowCells = (row: any): any[] =>
        Array.isArray(row) ? row : Array.isArray(row?.cells) ? row.cells : [];

      const columnCount = getRowCells(element.rows[0]).length || 1;
      const defaultWidth = Math.floor(100 / columnCount);

      const isBorderless =
        element.type === "table-borderless" || element.border === false;

      const rowsHtml = element.rows
        .map((row: any) => {
          const cells = getRowCells(row);
          if (!Array.isArray(cells)) return "";

          const cellsHtml = cells
            .map((cell: any) => {
              let cellContent = "";

              if (
                Array.isArray(cell?.paragraphs) &&
                cell.paragraphs.length > 0
              ) {
                cellContent = cell.paragraphs
                  .map((p: any) => renderSingleElementToHtml(p))
                  .join("");
              } else if (Array.isArray(cell?.runs) && cell.runs.length > 0) {
                cellContent = renderSingleElementToHtml({
                  type: "paragraph",
                  runs: cell.runs,
                  alignment: cell.alignment,
                });
              }

              // Fall back to the plain-text field whenever the richer
              // paragraphs/runs representations are absent or produced no
              // content, so data isn't silently dropped from the cell.
              if (!cellContent || isEffectivelyEmpty(cellContent)) {
                const cellText = (cell?.text || "").replace(/\n/g, "<br />");
                const alignStyle = cell?.alignment
                  ? ` style="text-align: ${cell.alignment};"`
                  : "";
                cellContent = `<p${alignStyle}>${cellText || "&nbsp;"}</p>`;
              }

              const width = cell.colwidth?.[0] ?? `${defaultWidth}%`;
              const cellStyles: string[] = [`width:${width}`];

              if (isBorderless) {
                cellStyles.push("border: none !important");
              }

              if (cell.backgroundColor) {
                cellStyles.push(
                  `background-color: ${cell.backgroundColor.startsWith("#") ? cell.backgroundColor : `#${cell.backgroundColor}`}`,
                );
              }

              if (cell.alignment) {
                cellStyles.push(`text-align: ${cell.alignment}`);
              }

              const styleAttr = ` style="${cellStyles.join(";")}"`;
              const colspan =
                cell.colspan && cell.colspan > 1
                  ? ` colspan="${cell.colspan}"`
                  : "";
              const rowspan =
                cell.rowspan && cell.rowspan > 1
                  ? ` rowspan="${cell.rowspan}"`
                  : "";

              return `<td${styleAttr}${colspan}${rowspan}>${cellContent}</td>`;
            })
            .join("");

          return `<tr>${cellsHtml}</tr>`;
        })
        .join("");

      const tableStyle = isBorderless
        ? ' style="border: none !important;"'
        : "";
      return `<table${tableStyle}><tbody>${rowsHtml}</tbody></table>`;
    }

    case "button": {
      return `<lsdb-button url="${element.url}" is_image="${element.is_image}"></lsdb-button>`;
    }

    case "image": {
      let src = element.blob_url || element.src || "";

      if (src && !src.startsWith("http") && !src.startsWith("data:")) {
        if (src.startsWith("PHN2Z")) {
          src = `data:image/svg+xml;base64,${src}`;
        } else if (src.startsWith("iVBOR")) {
          src = `data:image/png;base64,${src}`;
        } else if (src.startsWith("/9j/")) {
          src = `data:image/jpeg;base64,${src}`;
        }
      }

      const widthVal = element.width ? element.width : "50%";

      return `
          <img
              src="${src}"
              style="
                  width: ${widthVal};
                  max-width: 100%;
                  height: auto;
                  object-fit: contain;
                  display: block;
                  margin: 0 auto;
              "
          />
        `;
    }

    default:
      return "";
  }
};

// Marker class used to identify a legacy auto-inserted "Report Information"
// table (from an earlier iteration of this feature) in already-saved report
// content, so it can be stripped out on load — the cover page's own
// per-report-type tables (see reportInfoFieldAliases.ts) cover this now.
export const REPORT_INFO_TABLE_MARKER = "report-info-table";

export const splitTopLevelNodes = (html: string): string[] => {
  const container = document.createElement("div");
  container.innerHTML = html;
  return Array.from(container.children).map((el) => el.outerHTML);
};

// A table row can either be a plain array of cells or an object of shape
// { cells: [...] }. This returns the effective cell count either way, so
// header-row detection doesn't silently fail (and drop rows) when rows are
// objects, whose `.length` is always undefined.
export const getRowCellCount = (row: any): number =>
  Array.isArray(row)
    ? row.length
    : Array.isArray(row?.cells)
      ? row.cells.length
      : 0;

// The <lsdb-button> node's static HTML (produced by generateHTML for
// measurement purposes) is a bare, unstyled custom element with no
// children — browsers give unknown elements `display: inline` and zero
// height by default. Its real React node view actually renders as a
// ~36px tall flex row (small IconButton + padding). Without accounting for
// this, the pagination pre-pass thinks every "Pull from LSDB" button takes
// 0px, so it over-packs a page with content that doesn't actually fit once
// rendered live. The mismatch is only caught later by the runtime overflow
// splitter, which yanks the button (and its table) to the next page but
// leaves any short heading/label paragraphs above it behind — producing
// the large blank gap some pages show right after a page break.
const LSDB_BUTTON_MEASURED_HEIGHT_PX = 36;

export const measureHtmlHeight = (html: string, widthPx: number): number => {
  const container = document.createElement("div");
  container.className = "ProseMirror";
  container.style.width = `${widthPx}px`;
  container.style.visibility = "hidden";
  container.style.position = "absolute";
  container.style.top = "-9999px";
  container.style.boxSizing = "border-box";
  container.style.fontFamily = "Aptos, sans-serif";
  container.style.fontSize = "11pt";
  container.style.lineHeight = "1";
  const replaced = html.replace(
    /<lsdb-button\b[^>]*>[\s\S]*?<\/lsdb-button>/gi,
    `<div style="height:${LSDB_BUTTON_MEASURED_HEIGHT_PX}px;"></div>`,
  );
  container.innerHTML = replaced;

  document.body.appendChild(container);
  const height = container.scrollHeight;
  document.body.removeChild(container);
  return height;
};

export const isEffectivelyEmpty = (html: string) => {
  if (!html) return true;
  const stripped = html
    .replace(/<[^>]*>?/gm, "")
    .replace(/&nbsp;/g, "")
    .trim();
  return (
    stripped.length === 0 &&
    !html.includes("<img") &&
    !html.includes("<table") &&
    !html.includes("<lsdb-button")
  );
};

export const transformJsonToPageHtmlChunks = (
  data: any,
  contentHeightPx: number,
): string[] => {
  const rawElements = data?.elements || data?.data?.elements;
  if (!rawElements || !Array.isArray(rawElements) || rawElements.length === 0)
    return ["<p></p>"];

  const container = document.createElement("div");
  container.style.width = "633px";
  container.style.visibility = "hidden";
  container.style.position = "absolute";
  container.style.top = "-9999px";
  container.className = "ProseMirror";
  document.body.appendChild(container);

  const MAX_HEIGHT_PX = contentHeightPx - 10;
  const pageChunks: string[] = [];
  let currentChunkHtml = "";

  for (let i = 0; i < rawElements.length; i++) {
    const element = rawElements[i];
    const isTableType =
      element.type === "table" || element.type === "table-borderless";

    if (
      element.type === "button" &&
      i + 1 < rawElements.length &&
      (rawElements[i + 1].type === "table" ||
        rawElements[i + 1].type === "table-borderless")
    ) {
      const nextTable = rawElements[i + 1];
      const elementHtml = renderSingleElementToHtml(element);

      let headerRowCount = 1;
      if (
        nextTable.rows?.length > 1 &&
        getRowCellCount(nextTable.rows[0]) < getRowCellCount(nextTable.rows[1])
      ) {
        headerRowCount = 2;
      }

      const rowsToTest =
        nextTable.rows?.slice(
          0,
          Math.min(nextTable.rows.length, headerRowCount + 2),
        ) || [];

      if (rowsToTest.length > 0) {
        const testTableHtml = renderSingleElementToHtml({
          type: nextTable.type,
          rows: rowsToTest,
        });

        container.innerHTML = currentChunkHtml + elementHtml + testTableHtml;
        const chunkHasSubstance = !isEffectivelyEmpty(currentChunkHtml);

        if (container.scrollHeight > MAX_HEIGHT_PX && chunkHasSubstance) {
          pageChunks.push(currentChunkHtml);
          currentChunkHtml = elementHtml;
          continue;
        }
      }
    }

    if (isTableType) {
      let headerRowCount = 1;
      if (
        element.rows.length > 1 &&
        getRowCellCount(element.rows[0]) < getRowCellCount(element.rows[1])
      ) {
        headerRowCount = 2;
      }

      const headerRows = element.rows.slice(0, headerRowCount);
      const dataRows = element.rows.slice(headerRowCount);

      const minDataRowsRequired = Math.min(dataRows.length, 2);
      const initialTestRows = [
        ...headerRows,
        ...dataRows.slice(0, minDataRowsRequired),
      ];

      container.innerHTML =
        currentChunkHtml +
        renderSingleElementToHtml({
          type: element.type,
          rows: initialTestRows,
        });

      if (
        container.scrollHeight > MAX_HEIGHT_PX &&
        !isEffectivelyEmpty(currentChunkHtml)
      ) {
        pageChunks.push(currentChunkHtml);
        currentChunkHtml = "";
      }

      let currentTableRows = [...headerRows];

      for (const row of dataRows) {
        const testTableHtml = renderSingleElementToHtml({
          type: element.type,
          rows: [...currentTableRows, row],
        });

        container.innerHTML = currentChunkHtml + testTableHtml;

        if (
          container.scrollHeight > MAX_HEIGHT_PX &&
          currentTableRows.length > headerRowCount
        ) {
          currentChunkHtml += renderSingleElementToHtml({
            type: element.type,
            rows: currentTableRows,
          });
          pageChunks.push(currentChunkHtml);
          currentChunkHtml = "";
          currentTableRows = [...headerRows, row];
        } else {
          currentTableRows.push(row);
        }
      }

      if (currentTableRows.length > 0) {
        currentChunkHtml += renderSingleElementToHtml({
          type: element.type,
          rows: currentTableRows,
        });
      }
      continue;
    }

    const elementHtml = renderSingleElementToHtml(element);
    container.innerHTML = currentChunkHtml + elementHtml;

    const chunkHasSubstance = !isEffectivelyEmpty(currentChunkHtml);

    if (container.scrollHeight > MAX_HEIGHT_PX && chunkHasSubstance) {
      pageChunks.push(currentChunkHtml);
      currentChunkHtml = elementHtml;
    } else {
      currentChunkHtml += elementHtml;
    }
  }

  if (currentChunkHtml.length > 0) {
    pageChunks.push(currentChunkHtml);
  }

  document.body.removeChild(container);

  const validChunks = pageChunks.filter(
    (chunk) => chunk && chunk.trim().length > 0,
  );
  return validChunks.length > 0 ? validChunks : ["<p></p>"];
};

const getNodePlainText = (html: string): string => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || "").trim();
};

/**
 * Packs an ordered list of top-level HTML block strings (as produced by
 * `splitTopLevelNodes`) onto pages, honoring two "keep together" rules on
 * top of the standard height-based overflow check:
 *
 *  - A table (optionally preceded by an <lsdb-button>) is never split by
 *    row across pages — the whole cluster moves to the next page as a
 *    unit if it doesn't fit the remaining space.
 *  - A document's own literal "Table of Contents" block (a title line
 *    like "Table of Contents" plus its entries) is never split across
 *    pages, AND always ends its own page: a page break is forced
 *    immediately after it even when there's room left over, so no other
 *    section (e.g. a "Summary") ever ends up sharing the TOC's page.
 *    Works for any report type since detection only relies on the
 *    generic TOC title/entry text patterns (see headingUtils.ts), never
 *    on hardcoded section names.
 */
export const packNodesIntoPages = (
  nodes: string[],
  contentHeightPx: number,
  measureWidthPx = 640,
  firstPageContentHeightPx: number = contentHeightPx,
): string[] => {
  if (nodes.length === 0) return [];

  // The first output page may have a different content budget than the
  // rest (e.g. its header/footer chrome is hidden, freeing up extra
  // room) — `pageBudget()` returns the right one for whichever page is
  // currently being filled (chunks.length === 0 means we're still on the
  // very first page).
  const pageBudget = () =>
    chunks.length === 0 ? firstPageContentHeightPx : contentHeightPx;

  const nodeTexts = nodes.map(getNodePlainText);
  const tocBlockEndByStart = new Map<number, number>();
  findTocBlockRanges(nodeTexts).forEach(({ start, end }) => {
    const mergedHeight = measureHtmlHeight(
      nodes.slice(start, end + 1).join(""),
      measureWidthPx,
    );
    // Only keep the block atomic if it can actually fit on one empty
    // page; otherwise fall back to normal per-node packing so an
    // unusually long TOC doesn't break pagination altogether.
    if (mergedHeight <= Math.max(contentHeightPx, firstPageContentHeightPx)) {
      tocBlockEndByStart.set(start, end);
    }
  });

  // Fixed section boundaries (e.g. "Report Details", "Table of Contents")
  // always start a fresh page, regardless of how much room is left on the
  // current one — see isForcedPageStartText.
  const forcedPageStartIndices = new Set<number>();
  nodeTexts.forEach((text, idx) => {
    if (idx > 0 && isForcedPageStartText(text)) {
      forcedPageStartIndices.add(idx);
    }
  });

  const chunks: string[] = [];
  let currentChunk = "";

  const closePageIfTocBlock = (isTocBlock: boolean) => {
    if (isTocBlock && currentChunk.length > 0) {
      chunks.push(currentChunk);
      currentChunk = "";
    }
  };

  for (let i = 0; i < nodes.length; i++) {
    const blockEnd = tocBlockEndByStart.get(i);
    const isTocBlock = blockEnd !== undefined;
    const nodeHtml = isTocBlock
      ? nodes.slice(i, blockEnd + 1).join("")
      : nodes[i];
    const lastIndexOfNode = blockEnd ?? i;

    // 0. Forced section boundary: start this node on a brand-new page.
    if (forcedPageStartIndices.has(i) && !isEffectivelyEmpty(currentChunk)) {
      chunks.push(currentChunk);
      currentChunk = nodeHtml;
      i = lastIndexOfNode;
      closePageIfTocBlock(isTocBlock);
      continue;
    }

    const testHtml = currentChunk + nodeHtml;
    const height = measureHtmlHeight(testHtml, measureWidthPx);

    // 1. Standard overflow check
    if (height > pageBudget() && !isEffectivelyEmpty(currentChunk)) {
      chunks.push(currentChunk);
      currentChunk = nodeHtml;
      i = lastIndexOfNode;
      closePageIfTocBlock(isTocBlock);
      continue;
    }

    // 2. Orphan Prevention: Look ahead up to 2 elements for buttons/tables
    let lookaheadHtml = nodeHtml;
    let hasAttachedElement = false;

    for (let j = 1; j <= 2; j++) {
      const nextNode = nodes[lastIndexOfNode + j];
      if (
        nextNode &&
        (nextNode.includes("<table") || nextNode.includes("<lsdb-button"))
      ) {
        lookaheadHtml += nextNode;
        hasAttachedElement = true;
      } else {
        break;
      }
    }

    // If a cluster is detected, test their combined height
    if (hasAttachedElement) {
      const combinedHeight = measureHtmlHeight(
        currentChunk + lookaheadHtml,
        measureWidthPx,
      );

      if (
        combinedHeight > pageBudget() &&
        !isEffectivelyEmpty(currentChunk)
      ) {
        // The cluster exceeds the page limit. Push the current chunk early
        // so the text, button, and table jump to the next page together.
        chunks.push(currentChunk);
        currentChunk = nodeHtml;
        i = lastIndexOfNode;
        closePageIfTocBlock(isTocBlock);
        continue;
      }
    }

    currentChunk = testHtml;
    i = lastIndexOfNode;
    closePageIfTocBlock(isTocBlock);
  }

  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
};
